import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/LockOutlined';
import { useMsal } from '@azure/msal-react';
import { updateMyProfile } from '@upryzze/services';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { BusinessContext } from '../contexts/BusinessContext';
import { DEFAULT_API_SCOPES, getApiScopes } from '../auth/msalConfig';
import {
  useTokens,
  PageHeader,
  Surface,
  StatusPill,
} from '../components/primitives';

const CONTACT_PREFERENCE_OPTIONS = [
  { label: 'Email', value: 'email' },
  { label: 'Text', value: 'text' },
];

// Card section: header (title + subtitle), body, optional footer. Mirrors the
// settings panels in the redesign (Page 10): hairline-divided header, padded
// body, subtle-tinted footer for the primary action.
function Panel({ title, subtitle, action, children, footer, t }) {
  return (
    <Surface sx={{ overflow: 'hidden', mb: 2.25 }}>
      <Box
        sx={{
          p: '17px 22px',
          borderBottom: `1px solid ${t.hairline}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: t.text }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography sx={{ fontSize: 12, color: t.muted, mt: '2px' }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action ? <Box sx={{ ml: 'auto' }}>{action}</Box> : null}
      </Box>
      <Box sx={{ p: '20px 22px' }}>{children}</Box>
      {footer ? (
        <Box
          sx={{
            p: '13px 22px',
            borderTop: `1px solid ${t.hairline}`,
            bgcolor: t.subtle,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Surface>
  );
}

export default function Profile() {
  const t = useTokens();
  const { user, refreshUser } = useAuth();
  const { instance } = useMsal();
  const { showSnackbar } = useSnackbar();
  const { business } = useContext(BusinessContext);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [contactPreference, setContactPreference] = useState('email');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
    setPhone(user?.phone || '');
    setContactPreference(user?.contactPreference || 'email');
  }, [user]);

  const initials = useMemo(() => {
    const a = (firstName || '').trim()[0] || '';
    const b = (lastName || '').trim()[0] || '';
    const combined = `${a}${b}`.toUpperCase();
    if (combined) return combined;
    return (user?.email || '?').trim()[0]?.toUpperCase() || '?';
  }, [firstName, lastName, user]);

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await updateMyProfile({ firstName, lastName, phone, contactPreference });
      await refreshUser();
      setMessage({ type: 'success', text: 'Profile updated successfully.' });
      showSnackbar('Profile saved', 'success');
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        'Unable to save profile changes right now.';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    let scopes = DEFAULT_API_SCOPES;
    try {
      const resolvedScopes = await getApiScopes();
      if (Array.isArray(resolvedScopes) && resolvedScopes.length)
        scopes = resolvedScopes;
    } catch {
      scopes = DEFAULT_API_SCOPES;
    }

    await instance.loginRedirect({
      scopes,
      prompt: 'login',
      ...(user?.email ? { loginHint: user.email } : {}),
    });
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      bgcolor: t.card,
      fontSize: 13,
      '& fieldset': { borderColor: t.grayBorder },
    },
    '& .MuiInputLabel-root': { fontSize: 13.5 },
  };

  return (
    <Box sx={{ p: 3, maxWidth: 880, mx: 'auto' }}>
      <PageHeader
        title="Profile"
        sx={{ mb: 3 }}
        meta={
          business?.name ? (
            <StatusPill kind="neutral" label={business.name} />
          ) : null
        }
      />

      {message.text ? (
        <Alert
          severity={message.type || 'info'}
          sx={{ mb: 2.25, borderRadius: 2 }}
        >
          {message.text}
        </Alert>
      ) : null}

      <Panel
        t={t}
        title="Your profile"
        subtitle="This is how you appear across the console and on documents you send."
        footer={
          <>
            <Typography sx={{ fontSize: 11.5, color: t.faint }}>
              Changes apply across the console once saved.
            </Typography>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{
                ml: 'auto',
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '9px',
              }}
            >
              {saving ? (
                <>
                  <CircularProgress
                    size={15}
                    sx={{ color: 'inherit', mr: 1 }}
                  />
                  Saving
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: t.gradient,
              color: '#fff',
              fontWeight: 900,
              fontSize: 20,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {initials}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: t.text }}>
              {[firstName, lastName].filter(Boolean).join(' ') || 'Your name'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: t.muted }}>
              {user?.email || ''}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.75,
          }}
        >
          <TextField
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
            size="small"
            sx={fieldSx}
          />
          <TextField
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
            size="small"
            sx={fieldSx}
          />
          <TextField
            label="Email"
            value={user?.email || ''}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            helperText="Managed through your sign-in account"
            sx={fieldSx}
          />
          <TextField
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
            size="small"
            sx={fieldSx}
          />
          <TextField
            select
            label="Contact preference"
            value={contactPreference}
            onChange={(e) => setContactPreference(e.target.value)}
            fullWidth
            size="small"
            sx={fieldSx}
          >
            {CONTACT_PREFERENCE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Panel>

      <Panel t={t} title="Security" subtitle="Keep your account protected.">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              bgcolor: t.subtle,
              border: `1px solid ${t.cardBorder}`,
              display: 'grid',
              placeItems: 'center',
              color: t.muted,
            }}
          >
            <LockIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.text }}>
              Password
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: t.faint, mt: '2px' }}>
              Managed through Microsoft sign-in.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handleChangePassword}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              borderColor: t.grayBorder,
              color: t.textSecondary,
            }}
          >
            Change password
          </Button>
        </Box>
        <Typography sx={{ fontSize: 11.5, color: t.muted, mt: 2 }}>
          To change your password, continue to Microsoft sign-in and click
          &quot;Forgot password?&quot;. Two-factor authentication and active
          sessions are managed by your sign-in provider.
        </Typography>
      </Panel>
    </Box>
  );
}
